import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { RichTextEditor, extractQuotaErrorMessage, type TiptapDoc } from '@onesource/components';
import type { TCommentAttachment, TMentionUser } from '@onesource/schemas';
import {
  fetchSpaceMentionUsers,
  getPostCommentsQueryKey,
  getPresignedUploadUrl,
  useCreateComment,
  useUpdateComment,
} from '@onesource/services';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';

interface CommentInputProps {
  spaceId: string;
  postId: string;
  parentId?: string;
  commentId?: string;
  initialBody?: TiptapDoc | null;
  onClose?: () => void;
}

interface UploadingImage {
  file: File;
  preview: string;
  uploading: boolean;
  url?: string;
}

const uploadImage = async (file: File): Promise<string> => {
  const key = `${Date.now()}-${file.name}`;
  const { uploadUrl, publicUrl } = await getPresignedUploadUrl(key, file.type, file.size, {
    folder: 'community-hub-comments',
  });

  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) {
    throw new Error('Upload failed');
  }

  // Return the public URL or construct it
  return publicUrl || uploadUrl.split('?')[0];
};

export const CommentInput = ({ spaceId, postId, parentId, commentId, initialBody, onClose }: CommentInputProps) => {
  const isEditing = !!commentId;
  const fetchMentionUsers = useCallback(
    (query: string): Promise<TMentionUser[]> => fetchSpaceMentionUsers(spaceId, query),
    [spaceId],
  );
  const [body, setBody] = useState<TiptapDoc | null>(initialBody ?? null);
  const [images, setImages] = useState<UploadingImage[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const createComment = useCreateComment(postId);
  const updateComment = useUpdateComment(postId, commentId ?? '');

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Limit to 4 images
    const remainingSlots = 4 - images.length;
    const filesToUpload = [...files].slice(0, remainingSlots);

    for (const file of filesToUpload) {
      const preview = URL.createObjectURL(file);
      const newImage: UploadingImage = { file, preview, uploading: true };

      setImages(prev => [...prev, newImage]);

      try {
        const url = await uploadImage(file);
        setImages(prev => prev.map(img => (img.preview === preview ? { ...img, uploading: false, url } : img)));
      } catch (error) {
        // Remove failed upload and tell the user why (e.g. monthly 5MB personal upload limit)
        setImages(prev => prev.filter(img => img.preview !== preview));
        URL.revokeObjectURL(preview);
        enqueueSnackbar(extractQuotaErrorMessage(error) ?? 'Image upload failed', { variant: 'error' });
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (preview: string) => {
    setImages(prev => {
      const img = prev.find(i => i.preview === preview);
      if (img) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter(i => i.preview !== preview);
    });
  };

  const hasContent = !!body || images.length > 0;
  const isUploading = images.some(img => img.uploading);
  const canSubmit = hasContent && !isUploading && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing) {
        await updateComment.mutateAsync({ body: JSON.stringify(body) });
      } else {
        const attachments: TCommentAttachment[] = images
          .filter(img => img.url)
          .map(img => ({ url: img.url!, type: 'image' as const }));

        await createComment.mutateAsync({
          body: body ? JSON.stringify(body) : '',
          parentId,
          attachments: attachments.length > 0 ? attachments : undefined,
        });

        // Clear form
        setBody(null);
        images.forEach(img => URL.revokeObjectURL(img.preview));
        setImages([]);

        queryClient.invalidateQueries({ queryKey: getPostCommentsQueryKey(postId) });
      }

      onClose?.();
    } catch {
      // Handle error - could show a toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Image Previews */}
      {!isEditing && images.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {images.map(img => (
            <Box key={img.preview} sx={{ position: 'relative', width: 60, height: 60 }}>
              <Box
                component="img"
                src={img.preview}
                alt="Preview"
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  opacity: img.uploading ? 0.5 : 1,
                }}
              />
              {img.uploading && (
                <CircularProgress
                  size={20}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
              {!img.uploading && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(img.preview)}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'white',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Input Area */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <RichTextEditor
          value={body}
          onChange={setBody}
          onFetchMentionUsers={fetchMentionUsers}
          placeholder="Write a comment..."
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: isEditing ? 'flex-end' : 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {!isEditing && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={images.length >= 4}
                sx={{ color: '#808080', p: '4px' }}
              >
                <ImageIcon />
              </IconButton>
            </>
          )}

          <IconButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{ color: canSubmit ? '#ff073b' : '#ccc', p: '4px' }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
